"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

/* The concierge is on every page, and its module pulls in the Vercel AI
 * SDK + zod — 118 KB gzipped, in the homepage's initial script list, 96 KB
 * of it unused at load (Lighthouse mobile, 2026-09-06). Nobody chats in the
 * first seconds of a page view. So: the launcher and the engine mount on
 * the first pointer / key / scroll / touch, or after `delayMs` on a page
 * the visitor is simply reading. Everything the widget does — nudges, the
 * homepage auto-open, recently-viewed suggestions — still happens; it just
 * starts after the page has painted instead of competing with it. */
const ChatWidget = dynamic(() => import("./ChatWidget").then((m) => m.ChatWidget), {
  ssr: false,
});

export function ChatWidgetLazy({ delayMs = 8000 }: { delayMs?: number }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      setReady(true);
    };
    const timer = window.setTimeout(go, delayMs);
    /* pointerdown/keydown/touchstart are real intent — fire immediately.
     * "scroll" is not: virtually every visitor scrolls within the first
     * second of landing (and Lighthouse's own full-page screenshot pass
     * scrolls too), so a bare scroll listener defeated the whole point of
     * deferring the 158 KB AI SDK chunk — it loaded on almost every view
     * anyway, just as fast as if it weren't lazy at all. Require actual
     * scroll depth (past the hero) before treating it as engagement. */
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];
    for (const e of events) window.addEventListener(e, go, { passive: true, once: true });
    const SCROLL_DEPTH_PX = 600;
    const onScroll = () => {
      if (window.scrollY > SCROLL_DEPTH_PX) go();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, go);
      window.removeEventListener("scroll", onScroll);
    };
  }, [delayMs]);

  return ready ? <ChatWidget /> : null;
}
